---
title: 'The Do Not Sell or Share Link: Requirements and Real Examples'
description: 'What the opt-out link must say, where it has to appear, and the mistakes that make it non-compliant, including the one that quietly breaks it for most sites.'
slug: 'do-not-sell-my-personal-information-link'
publishedAt: '2026-10-19'
author: 'Syrcha'
tags: ['CCPA', 'US Privacy', 'Opt-Out']
readingMinutes: 7
draft: true
---

The opt-out link is the most visible obligation in US state privacy law and one of the most consistently mishandled. It looks like a footer link, so it gets treated like one: added once, never tested, quietly broken by the next tag deployment.

Here's what it actually has to do.

## When you need one

You need an opt-out link if you sell personal information, or share it for cross-context behavioural advertising, and you fall within a state law that requires it.

The trap is the word "sell". Under California's definition it covers disclosure for **other valuable consideration**, not just money. Running third-party advertising tags (a Meta pixel, an ad-network script, most retargeting) generally constitutes sale or sharing, whether or not any invoice exists.

Plenty of companies conclude they don't sell data, because nobody has ever wired them a payment for it. That's usually the wrong conclusion. Check what your tags transmit before deciding you're out of scope. Our [guide to US state privacy laws](/blog/us-state-privacy-laws-2026) covers the wider applicability tests.

## What the link must say

California recognises two forms:

- **"Do Not Sell or Share My Personal Information"**
- **"Your Privacy Choices"**, used together with the official opt-out icon

The second is the more practical choice for a multi-state footer, because it covers the broader set of opt-outs other states require without carrying California-specific wording. If you use it, use the actual published icon and not an approximation. A bespoke toggle graphic isn't the mark.

Wording that doesn't work: "Privacy Settings", "Cookie Preferences", "Manage My Data", "Ad Choices". None of these are the prescribed labels, and burying the opt-out inside a generic preferences dialog defeats the point of a clear and conspicuous link.

## Where it must appear

**On every page**: not only the homepage, and not only the privacy policy. A site-wide footer is the standard implementation.

**Clear and conspicuous.** Same size and colour as your other footer links is fine. Two points smaller in a lighter grey than everything around it isn't, and it's the most common way sites undermine an otherwise correct implementation.

**Reachable in one click** from wherever it appears, landing on something that actually processes the request.

**In your mobile app too**, if you have one. The obligation isn't web-only.

## What it must do when clicked

This is where most implementations fall down. The link is present, correctly labelled, in the footer of every page, and it opens a page that describes the right to opt out instead of exercising it.

The destination has to let the consumer **actually opt out**, and:

- **Not require an account.** You can't make opting out conditional on signing in.
- **Not demand unnecessary verification.** Opt-out requests are deliberately low-friction. Unlike deletion requests, they generally don't require identity verification. Asking for a government ID to stop targeted advertising is a violation, not diligence.
- **Not use confirmshaming or extra steps.** The same equal-prominence logic that governs cookie banners applies here.
- **Actually stop the sharing.** The opt-out has to propagate to your tags and downstream vendors, not just record a preference in your database.

That last one silently regresses. A tag added six months after launch, outside the consent gating, resumes the sharing the opt-out was supposed to have stopped.

## The requirement people forget: honour the signal too

A link on its own isn't sufficient in twelve states. California, Colorado, Connecticut, Delaware, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon and Texas require businesses to recognise universal opt-out mechanisms such as **Global Privacy Control**.

That signal arrives as a `Sec-GPC: 1` request header and is exposed to JavaScript as `navigator.globalPrivacyControl`. When present, it has to be treated as an opt-out request from that consumer and applied automatically, without a prompt and without asking them to also find your footer link.

One implementation note. The JavaScript property is `undefined` in browsers that don't implement GPC, which today means Chrome, Safari and Edge. Only Brave, Firefox and DuckDuckGo send it natively, and Firefox needs it switched on first. Check for the value being exactly `true`, and don't read its absence as the user declining. California's AB 566 requires browsers sold in the state to offer a built-in opt-out from 1 January 2027, so the share of traffic carrying it is about to rise sharply.

California's enforcement record makes the point. The Sephora settlement in 2022 turned on failure to honour GPC and carried a 1.2 million dollar penalty. A perfect footer link doesn't cure an ignored signal.

## A five-minute audit

1. **Is the link on every page**, including deep pages and blog posts?
2. **Does it use a recognised label**: "Do Not Sell or Share My Personal Information", or "Your Privacy Choices" with the official icon?
3. **Is it as visually prominent** as the links beside it?
4. **Does the destination let you opt out**, instead of describing the right?
5. **Can you complete it without logging in** or providing identity documents?
6. **Do advertising tags actually stop** after opting out? Check the network tab, not the confirmation message.
7. **Does a `Sec-GPC: 1` header produce the same result** without any clicks?

Steps 6 and 7 are the ones that fail in practice, and neither is visible from the page itself.

## The takeaway

The link is the easy part. The obligation is the outcome: a consumer who wants to stop the sharing can stop it, in one step, without an account, and a browser signalling the same preference is obeyed without being asked twice.

Test it from the outside, network tab open and GPC enabled, because that's exactly how someone else will test it.

---

_This article is general information, not legal advice. Requirements vary by state, and California maintains the most detailed regulations on link text, placement and the official opt-out icon._

**Sources:** [California Privacy Protection Agency: CCPA regulations](https://cppa.ca.gov/regulations/) · [Tannenbaum Helpern: Universal opt-out mechanisms and Global Privacy Control](https://www.thsh.com/publications/universal-opt-out-mechanisms-and-global-privacy-control-state-law-requirements-and-compliance-guidance/) · [Global Privacy Control specification](https://globalprivacycontrol.org/)
